import type { CreateSurchargeRequest, Ratebook, Region, Surcharge } from '@api/useFreight/types'
import { useRateManagement } from '@api/useFreight/useFreight'

import { Form, message } from 'antd'
import { useState, useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import type { SurchargeFormValues } from '../types'

/**
 * Custom hook for managing surcharge creation and editing
 * @param onSuccess Callback after successful creation/edit
 * @param onClose Callback to close the drawer
 * @returns Object containing form and submission handlers
 */
export const useCreateSurcharge = (
  onSuccess: (created: Surcharge) => void,
  onClose: () => void,
  ratebook: Ratebook
) => {
  const { useFilteredMetadataQuery } = useRateManagement()
  const { data: metadataOptions } = useFilteredMetadataQuery(ratebook)
  const [form] = Form.useForm<SurchargeFormValues>()
  const queryClient = useQueryClient()
  const { useCreateSurcharge } = useRateManagement()
  const createMutation = useCreateSurcharge()
  const [hasEndDate, setHasEndDate] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const formInitializedRef = useRef(false)

  // Clear surcharge value when type changes
  const rateType = Form.useWatch('type', form) || 'Percentage'

  /**
   * Initialize form for editing or creating
   * @param visible Whether the drawer is visible
   * @param editingSurcharge The surcharge being edited (if any)
   */
  const initializeForm = (visible: boolean, editingSurcharge?: Surcharge | null) => {
    // If the drawer is not visible, reset the initialization flag
    if (!visible) {
      formInitializedRef.current = false
      return
    }

    // If form is already initialized for the current session, don't reinitialize
    if (formInitializedRef.current) {
      return
    }

    if (visible && editingSurcharge) {
      // Set form values from the editing surcharge
      form.setFieldsValue({
        freight_region: editingSurcharge.freight_region_name || undefined,
        freight_region_id: editingSurcharge.freight_region_id || undefined,
        product_group: editingSurcharge.product_group || undefined,
        type: editingSurcharge.type || 'Percentage',
      })

      // Mark the form as initialized
      formInitializedRef.current = true
    } else if (visible) {
      // Reset form for create mode
      form.resetFields()
      form.setFieldsValue({
        effective_from: dayjs(),
        type: 'Percentage',
      })
      setHasEndDate(false)

      // Mark the form as initialized
      formInitializedRef.current = true
    }
  }

  /**
   * Handle form submission
   * @param values The form values
   * @param ratebook The current ratebook
   * @param editingSurcharge The surcharge being edited (if any)
   */
  const handleSubmit = async (values: SurchargeFormValues, ratebook: Ratebook, editingSurcharge?: Surcharge | null) => {
    if (values.freight_region) {
      values.freight_region_id = metadataOptions?.freight_regions?.find(
        (option: Region) => option.name === values.freight_region
      )?.id
    }

    try {
      setSubmitting(true)
      const now = dayjs().toISOString()

      let formattedData = {}

      if (editingSurcharge) {
        formattedData = {
          ...editingSurcharge,
          counterparty_id: ratebook.counterparty_id,
          counterparty: ratebook.counterparty_name,
          book_type: ratebook.book_type,
          freight_region_name: values.freight_region,
          freight_region_id: values.freight_region_id,
          product_group: values.product_group,
          type: values.type,
        }
      } else {
        const effectiveFrom = dayjs(values.effective_from).startOf('day').toISOString()

        // Convert percentage to decimal (e.g., 20% -> 0.2)
        const surchargeValue = values.type === 'Percentage' ? Number(values.surcharge) / 100 : Number(values.surcharge)

        formattedData = {
          counterparty_id: ratebook.counterparty_id,
          counterparty: ratebook.counterparty_name,
          book_type: ratebook.book_type,
          freight_region_name: values.freight_region,
          freight_region_id: values.freight_region_id,
          product_group: values.product_group,
          type: values.type,
          surcharge: surchargeValue,
          current_surcharge: surchargeValue,
          effective_from: effectiveFrom,
          rates: [
            {
              effective_from: effectiveFrom,
              effective_to:
                hasEndDate && values.effective_to ? dayjs(values.effective_to).startOf('day').toISOString() : null,
              surcharge: surchargeValue,
              created_by: localStorage.getItem('user_email') || 'system',
              created_on: now,
            },
          ],
        }
      }

      const created = await createMutation.mutateAsync(formattedData as CreateSurchargeRequest)
      await queryClient.invalidateQueries({ queryKey: ['surcharges'] })
      message.success(`Surcharge ${editingSurcharge ? 'updated' : 'created'} successfully`)
      onSuccess?.(created)
      onClose?.()
      form.setFieldValue('surcharge', undefined)
      form.resetFields()
    } catch (error) {
      console.error(`${editingSurcharge ? 'Update' : 'Create'} surcharge error:`, error)
      message.error(`Failed to ${editingSurcharge ? 'update' : 'create'} surcharge`)
    } finally {
      setSubmitting(false)
    }
  }

  /**
   * Handle form submission button click
   */
  const handleFormSubmit = () => {
    form.submit()
  }

  /**
   * Handle drawer close
   */
  const handleClose = () => {
    formInitializedRef.current = false
    onClose?.()
  }

  return {
    form,
    hasEndDate,
    setHasEndDate,
    submitting,
    rateType,
    initializeForm,
    handleSubmit,
    handleFormSubmit,
    handleClose,
  }
}
